import React, { useEffect, useMemo, useState } from "react";
import "./CheckOut.css";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useCreatePaymentMutation } from "../../../Redux/Api/AuthApi";
import useAuth from "../../../MiddleWay/useAuth";
import Navbar from "../Navbar/Navbar"

const CheckOut = () => {
  const dispatch = useDispatch();
  const { id: userId } = useAuth();
  const { membershipPlan } = useSelector((state) => state.Auth);
  const navigate = useNavigate();

  const [createPayment, { isLoading }] = useCreatePaymentMutation();

  // Form State
  const [formData, setFormData] = useState({
    email: "",
    emailChecked: false,
    payment_method: "", // "card" or "momo"
    phoneNumber: "",
  });

  const [error, setError] = useState("");

  // Extract and calculate values safely
  const price = useMemo(() => {
    if (!membershipPlan?.price) return 0;
    // Remove non-numeric characters and parse
    return Number(membershipPlan.price.toString().replace(/[^0-9.]/g, "")) || 0;
  }, [membershipPlan?.price]);

  const childCount = Number(membershipPlan?.childCount) || 1;
  const subtotal = price * childCount;

  const taxRate = 0.03; // 3%
  const taxAmount = useMemo(() => subtotal * taxRate, [subtotal]);
  const total = useMemo(() => subtotal + taxAmount, [subtotal, taxAmount]);

  // Initialize form with user data if available
  useEffect(() => {
    if (membershipPlan) {
      setFormData((prev) => ({
        ...prev,
        email: membershipPlan.email || prev.email,
      }));
    }
  }, [membershipPlan]);

  // Handle input changes
  const handleFormChange = (e) => {
    const { name, type, value, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setError(""); // Clear error when user types
  };

  // Submit Handler
  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!formData.payment_method) {
      setError("Please select a payment method");
      return;
    }

    if (formData.payment_method === "momo" && !formData.phoneNumber) {
      setError("Please enter your MTN MoMo number");
      return;
    }

    const payload = {
      userId,
      planId: membershipPlan?.id,
      planName: membershipPlan?.name,
      childCount,
      price,
      subtotal,
      taxAmount: Math.round(taxAmount),
      totalAmount: Math.round(total),
      email: formData.email,
      emailChecked: formData.emailChecked,
      payment_method: formData.payment_method,
      phoneNumber: formData.phoneNumber,
    };

    try {
      const response = await createPayment(payload).unwrap();

      if (formData.payment_method === "card") {
        const { pesResponse } = response?.data || response;

        if (pesResponse?.redirect_url) {
          window.location.href = pesResponse.redirect_url;
          return;
        }
      }

      // For MoMo or other cases
      console.log("Payment Response:", response);
      alert("Payment initiated successfully!");
      // navigate to success page if needed
    } catch (err) {
      console.error(err);
      setError(
        err?.data?.message ||
          err?.message ||
          "Payment initialization failed. Please try again."
      );
    }
  };

  return (
    <div className="bone-checkout-container">
      <Navbar/>
      <main className="bone-checkout-main">
        {/* LEFT: Checkout Form */}
        <section className="bone-checkout-form">
          <form onSubmit={handlePaymentSubmit} className="checkout-form-inner">
            {/* Contact Section */}
            <div className="form-section">
              <div className="section-header">
                <h3>Contact Information</h3>
              </div>

              <label className="input-label">
                Email Address
                <input
                  name="email"
                  type="email"
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={handleFormChange}
                  required
                />
              </label>

              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="emailChecked"
                  checked={formData.emailChecked}
                  onChange={handleFormChange}
                />
                <span>Email me with news and offers</span>
              </label>
            </div>

            {/* Booked By */}
            <div className="form-section">
              <div className="section-header">
                <h3>Booked By</h3>
              </div>
              <label className="input-label">
                Parent / Guardian Email
                <input
                  name="email"
                  type="email"
                  placeholder="parent@example.com"
                  value={formData.email}
                  onChange={handleFormChange}
                  required
                />
              </label>
            </div>

            {/* Payment Method */}
            <div className="form-section">
              <div className="section-header">
                <h3>Payment Method</h3>
                <p className="muted">All transactions are secure and encrypted.</p>
              </div>

              <div className="payment-options">
                <label className="payment-option">
                  <input
                    type="radio"
                    name="payment_method"
                    value="card"
                    checked={formData.payment_method === "card"}
                    onChange={handleFormChange}
                  />
                  <div className="payment-meta">
                    <strong>Pesapal (Card)</strong>
                    <div className="card-logos">
                      <img src="/images/visa.png" alt="Visa" />
                      <img src="/images/master.png" alt="Mastercard" />
                    </div>
                  </div>
                </label>

                <label className="payment-option">
                  <input
                    type="radio"
                    name="payment_method"
                    value="momo"
                    checked={formData.payment_method === "momo"}
                    onChange={handleFormChange}
                  />
                  <div className="payment-meta">
                    <strong>MTN MoMo</strong>
                    <div className="muted small">Mobile Money</div>
                  </div>
                </label>
              </div>

              {/* MoMo Phone Number */}
              {formData.payment_method === "momo" && (
                <label className="input-label">
                  MoMo Phone Number
                  <input
                    name="phoneNumber"
                    type="tel"
                    placeholder="+250 78X XXX XXX"
                    value={formData.phoneNumber}
                    onChange={handleFormChange}
                    required
                  />
                </label>
              )}

              {/* Pesapal Info */}
              {formData.payment_method === "card" && (
                <div className="payment-help">
                  <p>
                    After clicking <strong>Pay Now</strong>, you will be redirected to Pesapal to complete your payment securely.
                  </p>
                </div>
              )}

              {error && <div className="error-msg">{error}</div>}

              {formData.payment_method && (
                <div className="form-actions">
                  <button
                    type="submit"
                    className="submit-btn"
                    disabled={isLoading}
                  >
                    {isLoading
                      ? "Processing Payment..."
                      : `Pay Rwf ${Math.round(total).toLocaleString()}`}
                  </button>
                </div>
              )}
            </div>
          </form>
        </section>

        {/* RIGHT: Order Summary */}
        <aside className="bone-checkout-summary">
          <h3 className="summary-title">Order Summary</h3>

          <div className="summary-items">
            {membershipPlan ? (
              <div className="summary-item">
                <div className="item-thumb">
                  <img src="/images/membership.webp" alt={membershipPlan.name} />
                </div>
                <div className="item-meta">
                  <div className="item-name">{membershipPlan.name}</div>
                  <div className="item-qty">Children: {childCount}</div>
                </div>
                <div className="item-price">Rwf {subtotal}</div>
              </div>
            ) : (
              <p className="muted">No plan selected</p>
            )}
          </div>

          <div className="summary-totals">
            <div className="tot-row">
              <span>Subtotal</span>
              <span>Rwf {subtotal}</span>
            </div>
            <div className="tot-row">
              <span>Tax (3%)</span>
              <span>Rwf {Math.round(taxAmount)}</span>
            </div>
            <div className="tot-row total">
              <strong>Total</strong>
              <strong>Rwf {Math.round(total)}</strong>
            </div>
          </div>
        </aside>
      </main>
    </div>
  );
};

export default CheckOut;